/** Trip overview map — port of initMap(): region filter chips, numbered stay
 * pins, colored activity dots with popups → details card, dashed ferry route,
 * tile-failure notice, Safari/iPad invalidateSize workarounds. */
import L from "leaflet";
import { esc } from "@trip/shared/engine/time";
import { store } from "../store.js";

let map: L.Map | null = null;
const groups: Record<string, L.LayerGroup> = {};
/** removed and re-added on every init — otherwise each re-render leaks a pair */
let mapResize: (() => void) | null = null;
let mapOrient: (() => void) | null = null;

export function initTripMap() {
  const def = store.def;
  // trips without Croatia's map card render no #map element at all
  const el = document.getElementById("map");
  if (!el) return;
  el.innerHTML = "";
  if (map) {
    map.remove();
    map = null;
  }
  if (mapResize) window.removeEventListener("resize", mapResize);
  if (mapOrient) window.removeEventListener("orientationchange", mapOrient);
  // frame whatever this trip actually pins — hardcoded coordinates would show
  // every non-Croatia trip an empty Adriatic
  const pts: [number, number][] = [
    ...Object.values(store.def.geo).map((g) => [g[0], g[1]] as [number, number]),
    ...store.def.stays.map((s) => [s[2], s[3]] as [number, number]),
  ];
  map = L.map("map", { scrollWheelZoom: false });
  if (pts.length) map.fitBounds(L.latLngBounds(pts).pad(0.2));
  else map.setView([43.2, 16.9], 8); // pinless trip: any stable default will do
  const tiles = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 18,
    attribution: "&copy; OpenStreetMap contributors",
  });
  tiles.addTo(map);

  /* Safari/iPad: container can be zero-height or half-laid-out at init */
  const fix = () => map?.invalidateSize();
  setTimeout(fix, 300);
  setTimeout(fix, 1200);
  mapOrient = () => setTimeout(fix, 400);
  mapResize = fix;
  window.addEventListener("orientationchange", mapOrient);
  window.addEventListener("resize", mapResize);
  if ("IntersectionObserver" in window) {
    new IntersectionObserver((es, obs) => {
      es.forEach((e) => {
        if (e.isIntersecting) {
          fix();
          obs.disconnect();
        }
      });
    }).observe(el);
  }

  /* offline tiles: show a hint instead of a silent gray box */
  let tileOk = 0,
    tileErr = 0;
  tiles.on("tileload", () => {
    tileOk++;
    document.getElementById("tilewarn")?.remove();
  });
  tiles.on("tileerror", () => {
    tileErr++;
    if (tileOk === 0 && tileErr >= 2 && !document.getElementById("tilewarn")) {
      el.insertAdjacentHTML(
        "beforeend",
        '<div id="tilewarn" style="position:absolute; inset:auto 10px 10px 10px; z-index:900; background:rgba(255,255,255,.95); border:1px solid #eccfc5; color:#a33f2c; border-radius:9px; padding:9px 13px; font-size:12.5px;">⚠ Map background couldn\'t load — you seem to be offline. Pins and the itinerary still work; tiles return with a connection.</div>'
      );
    }
  });

  for (const r of Object.keys(def.rc)) groups[r] = L.layerGroup().addTo(map);
  const stayLayer = L.layerGroup().addTo(map);
  L.polyline(def.route, { color: "#2e7d9e", weight: 2.5, dashArray: "6 7", opacity: 0.75 }).addTo(map);

  for (const [id, [lat, lng, reg]] of Object.entries(def.geo)) {
    const a = def.act[id];
    if (!a) continue;
    const m = L.marker([lat, lng], {
      icon: L.divIcon({ className: "", html: `<span class='mdot' style='background:${def.rc[reg]}'></span>`, iconSize: [14, 14], iconAnchor: [7, 7] }),
    });
    const p = (a.price || "").split(/[—·]/)[0].trim();
    m.bindPopup(
      `<b>${esc(a.name)}</b><br><span style='font-size:11px;color:#6b7a86'>${esc(a.cat || "")}</span>` +
        (p ? `<br><span style='color:#c0653f;font-weight:700'>${esc(p)}</span>` : "") +
        (a.nomodal
          ? ""
          : `<br><button class='btn-det btn-sm' style='margin-top:7px' data-action="modal" data-id="${id}">Full details</button>`)
    );
    groups[reg].addLayer(m);
  }

  for (const [n, label, lat, lng] of def.stays) {
    L.marker([lat, lng], {
      icon: L.divIcon({ className: "", html: `<span class='mstay'>${n}</span>`, iconSize: [25, 25], iconAnchor: [12, 12] }),
      zIndexOffset: 500,
    })
      .bindPopup(`<b>${esc(label)}</b>`)
      .addTo(stayLayer);
  }

  const fl = document.getElementById("mapfilters")!;
  fl.innerHTML = Object.keys(def.rnames)
    .map((k) => `<button class='daychip${k === "all" ? " on" : ""}' data-r='${k}'>${def.rnames[k]}</button>`)
    .join("");
  fl.querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => {
      fl.querySelectorAll("button").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      const r = (b as HTMLElement).dataset.r!;
      for (const [k, g] of Object.entries(groups)) {
        if (r === "all" || k === r) map!.addLayer(g);
        else map!.removeLayer(g);
      }
      if (r === "all") {
        const all = Object.values(groups).flatMap((g) => g.getLayers());
        if (all.length) map!.fitBounds(L.featureGroup(all as L.Layer[]).getBounds().pad(0.2));
      } else {
        const ms = groups[r].getLayers();
        if (ms.length) map!.fitBounds(L.featureGroup(ms as L.Layer[]).getBounds().pad(0.35));
      }
    })
  );
}
